import dayjs from 'dayjs';

import type { ModelIncomesReport } from '@app/api/Analytics';
import type { ColumnDataType } from '@app/pages/Analytics/RevenueStatement/types';
import { divideAmount } from '@app/utils';

const normalizeAccountName = (name: string) => name.replace(/\s+/g, ' ').trim();
const keyFor = (type: 'cash' | 'cashless', name: string) =>
  `${type}::${normalizeAccountName(name)}`;

const generateGroupedColumns = (
  cashAccounts: string[],
  cashlessAccounts: string[]
) => {
  return [
    {
      title: 'Дата',
      dataIndex: 'date',
      fixed: 'left' as const,
      className: 'date-column',
      width: 120,
    },
    {
      title: 'Наличные',
      children: cashAccounts.map((acc) => ({
        title: acc,
        dataIndex: keyFor('cash', acc),
        className: 'amount-column',
      })),
    },
    {
      title: 'Безнал',
      children: cashlessAccounts.map((acc) => ({
        title: acc,
        dataIndex: keyFor('cashless', acc),
        className: 'amount-column',
      })),
    },
    {
      title: 'Итого',
      dataIndex: 'total',
      className: 'amount-column',
    },
  ];
};

export const transformRevenueStatementToTableData = (
  data: ModelIncomesReport | undefined
): {
  dataSource: ColumnDataType[];
  columns: ReturnType<typeof generateGroupedColumns>;
} => {
  if (!data) {
    return { dataSource: [], columns: [] };
  }

  const rows = data.Rows ?? [];
  const cashAccountsSet = new Set<string>();
  const cashlessAccountsSet = new Set<string>();

  rows.forEach((row) => {
    row.Cash.forEach((item) =>
      cashAccountsSet.add(normalizeAccountName(item.PaymentAccount))
    );
    row.Cashless.forEach((item) =>
      cashlessAccountsSet.add(normalizeAccountName(item.PaymentAccount))
    );
  });

  data.Totals.Cash.forEach((item) =>
    cashAccountsSet.add(normalizeAccountName(item.PaymentAccount))
  );
  data.Totals.Cashless.forEach((item) =>
    cashlessAccountsSet.add(normalizeAccountName(item.PaymentAccount))
  );

  const cashAccounts = Array.from(cashAccountsSet);
  const cashlessAccounts = Array.from(cashlessAccountsSet);

  const dataSource: ColumnDataType[] = rows.map((row, index) => {
    const rowData: ColumnDataType = {
      key: `${index}`,
      date: dayjs(row.Date, 'DD-MM-YYYY').format('DD.MM.YYYY'),
      total: divideAmount(row.Total),
    };

    row.Cash.forEach((item) => {
      rowData[keyFor('cash', item.PaymentAccount)] = divideAmount(item.Income);
    });
    row.Cashless.forEach((item) => {
      rowData[keyFor('cashless', item.PaymentAccount)] = divideAmount(
        item.Income
      );
    });

    return rowData;
  });

  const totalsRow: ColumnDataType = {
    key: 'total',
    date: 'Итого',
    total: divideAmount(data.Totals.Total),
  };

  cashAccounts.forEach((account) => {
    totalsRow[keyFor('cash', account)] = '0';
  });
  cashlessAccounts.forEach((account) => {
    totalsRow[keyFor('cashless', account)] = '0';
  });

  data.Totals.Cash.forEach((item) => {
    totalsRow[keyFor('cash', item.PaymentAccount)] = divideAmount(item.Income);
  });
  data.Totals.Cashless.forEach((item) => {
    totalsRow[keyFor('cashless', item.PaymentAccount)] = divideAmount(
      item.Income
    );
  });

  dataSource.push(totalsRow);

  const columns = generateGroupedColumns(cashAccounts, cashlessAccounts);

  return { dataSource, columns };
};
